import { StatusBar, StatusBarStyle } from 'expo-status-bar'
import { observer } from 'mobx-react-lite'
import React, { useState } from 'react'
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  SafeAreaView,
  TextInput,
  TouchableOpacity,
  View
} from 'react-native'
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router'

import { Text } from '../../components'
import { MainHeader } from '../../components/MainHeader/MainHeader'
import { translate } from '../../i18n'
import { useStore } from '../../models'
import { EditableFields } from './EditProfileSheet'
import { styles } from './styles'
import { DS } from '../../design-system'

export const EditProfileScreen = observer(() => {
  const router = useRouter()
  const { editingFieldName } = useLocalSearchParams<{ editingFieldName: EditableFields }>()
  const isEditingName = editingFieldName || EditableFields.email

  const store = useStore()
  const [userName, setUserName] = useState(store.session.user ? store.session.user.name : '')
  const [userEmail, setUserEmail] = useState(store.session.user ? store.session.user.email : '')
  const [isLoading, setIsLoading] = React.useState(false)
  const [emailError, setEmailError] = React.useState('')

  useFocusEffect(
    React.useCallback(() => {
      setEmailError('')
    }, [])
  )

  const content =
    isEditingName === EditableFields.name
      ? {
          question: translate('editProfileScreen.nameQuestion'),
          label: translate('editProfileScreen.nameLabel'),
          placeholder: translate('editProfileScreen.namePlaceholder'),
          currentValue: userName
        }
      : {
          question: translate('editProfileScreen.emailQuestion'),
          label: translate('editProfileScreen.emailLabel'),
          placeholder: translate('editProfileScreen.emailPlaceholder'),
          currentValue: userEmail
        }

  useFocusEffect(
    React.useCallback(() => {
      if (store.session.user) {
        const playerName = store.session.user.name
        const userEmail = store.session.user.email
        setUserName(playerName)
        setUserEmail(userEmail)
      }
    }, [store.session.user])
  )

  const goBack = () => {
    router.replace('/profile')
  }

  const validateEmail = (email: string) => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
    if (!emailRegex.test(email)) {
      return false
    }
    return true
  }

  const updateUserField = async () => {
    try {
      setEmailError('')
      setIsLoading(true)
      if (isEditingName === EditableFields.name) {
        if (userEmail && userEmail !== '') {
          await store.session.updateUserData(userName, userEmail)
          setIsLoading(false)
          router.replace('/profile')
        }
      } else if (validateEmail(userEmail ?? '') && userEmail) {
        setIsLoading(false)
        await store.session.sendNewEmail(userEmail)
        router.navigate(`/otp-email-verification?updatedEmail=${userEmail}`)
      } else {
        setIsLoading(false)
        setEmailError('*Enter a valid email address')
      }
    } catch (error) {
      //@ts-ignore
      setEmailError(error?.response?.errors[0]?.message)
      setIsLoading(false)
    }
  }

  return (
    <SafeAreaView style={[styles.container, { paddingTop: Platform.OS === 'android' ? 25 : 0 }]}>
      <StatusBar animated={true} style={'dark' as StatusBarStyle} hidden={false} />
      <MainHeader
        leftPress={goBack}
        title={isEditingName === EditableFields.name ? 'Edit Name' : 'Edit Email'}
        titleStyle={{ color: '#121212' }}
        headerContainerStyle={styles.headerContainerStyle}
      />
      <KeyboardAvoidingView
        keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 50}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={{ flex: 1 }}
      >
        <View style={{ flex: 1, paddingHorizontal: 24 }}>
          <Text style={styles.question}>{content.question}</Text>
          <Text style={styles.nameText}>{content.label}</Text>
          <TextInput
            placeholder={content.placeholder}
            placeholderTextColor={'#68706D'}
            style={styles.inputField}
            allowFontScaling={false}
            autoCorrect={false}
            maxLength={50}
            autoComplete="off"
            autoCapitalize="none"
            value={content.currentValue ?? ''}
            onChangeText={(value: string) => {
              if (isEditingName === EditableFields.name) {
                setUserName(value)
              } else {
                setUserEmail(value)
              }
            }}
            autoFocus={true}
          />
          {emailError ? (
            <DS.Text variant="Body Small/Regular" color="Utility/Warning" style={styles.error}>
              {emailError}
            </DS.Text>
          ) : null}
          <TouchableOpacity
            disabled={(userName && userName.trim().length === 0) || userName === '' || userEmail === ''}
            onPress={updateUserField}
            style={[
              styles.buttonStyle,
              { opacity: userName === '' || (userName && userName.trim().length === 0) || userName === '' ? 0.5 : 1 }
            ]}
          >
            <View style={styles.buttonView}>
              <Text
                tx={isEditingName === EditableFields.name ? 'common.done' : 'common.next'}
                style={styles.buttonText}
              />
              {isLoading ? <ActivityIndicator size="small" color="#FFFFFF" style={styles.loadingStyle} /> : null}
            </View>
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  )
})
